import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { STLLoader } from 'three/addons/loaders/STLLoader.js';
import { OBJLoader } from 'three/addons/loaders/OBJLoader.js';
import { ThreeMFLoader } from 'three/addons/loaders/3MFLoader.js';

document.querySelectorAll('.ap-service').forEach((root) => {
  if (root.dataset.initialized === 'true') return;
  root.dataset.initialized = 'true';
  const $ = (selector) => root.querySelector(selector);
  const viewer = $('[data-viewer]');
  const maxBytes = Number(root.dataset.maxFileMb || 25) * 1024 * 1024;
  const allowed = new Set(['stl','3mf','obj','step','stp','iges','igs','amf']);
  const previewable = new Set(['stl','3mf','obj']);
  const filesInput = $('[data-files]');
  let scene, camera, renderer, controls, currentObject = null, animationId = null;
  let selectedFiles = [];
  let currentFile = null;
  let selectedColour = 'No preference – please advise';
  let turnstileWidgetId = null;
  let turnstileToken = '';
  let uploadedFiles = [];
  const uploadEndpoint = root.dataset.uploadEndpoint;
  const sitekey = root.dataset.turnstileSitekey;
  const colourMap = {
    'Black': '#171717', 'White': '#f4f4f0', 'Blue': '#2463a6', 'Yellow': '#f2d12c',
    'Orange': '#ed8128', 'Red': '#c93636', 'Pink': '#e99ab9', 'Pine Green': '#315b45',
    'Light Green': '#a9c99a', 'Light Purple': '#b7a2d5', 'Grey': '#929894'
  };

  function error(message) {
    const el = $('[data-error]');
    el.textContent = message;
    el.hidden = false;
  }
  function clearError() { $('[data-error]').hidden = true; $('[data-error]').textContent = ''; }

  function initViewer() {
    scene = new THREE.Scene();
    scene.background = new THREE.Color('#f1f4f0');
    camera = new THREE.PerspectiveCamera(40, 1, 0.1, 10000);
    camera.position.set(150, 120, 180);
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    viewer.appendChild(renderer.domElement);
    controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    scene.add(new THREE.HemisphereLight(0xffffff, 0x879184, 2));
    const light = new THREE.DirectionalLight(0xffffff, 2);
    light.position.set(120, 180, 100);
    scene.add(light);
    scene.add(new THREE.GridHelper(300, 30, 0xd6ddd5, 0xe7ebe5));
    resize();
    const render = () => {
      animationId = requestAnimationFrame(render);
      controls.update();
      renderer.render(scene, camera);
    };
    render();
    if ('ResizeObserver' in window) new ResizeObserver(resize).observe(viewer);
    else window.addEventListener('resize', resize);
  }
  function resize() {
    if (!renderer || !camera) return;
    const w = Math.max(1, viewer.clientWidth), h = Math.max(1, viewer.clientHeight);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h, false);
  }
  function disposeObject(object) {
    if (!object) return;
    object.traverse((child) => {
      if (child.geometry) child.geometry.dispose();
      if (child.material) {
        const materials = Array.isArray(child.material) ? child.material : [child.material];
        materials.forEach((material) => material.dispose());
      }
    });
  }
  function frameObject(object, size) {
    const max = Math.max(size.x, size.y, size.z, 1);
    camera.position.set(max * 1.4, max * 1.1, max * 1.8);
    camera.near = Math.max(0.01, max / 1000);
    camera.far = max * 30;
    camera.updateProjectionMatrix();
    controls.target.set(0, 0, 0);
    controls.minDistance = max * 0.25;
    controls.maxDistance = max * 8;
    controls.update();
  }
  function updateModelMetrics(file) {
    if (!currentObject) return;
    const box = new THREE.Box3().setFromObject(currentObject);
    const size = box.getSize(new THREE.Vector3());
    $('[data-dimensions]').textContent = [size.x,size.y,size.z].map(v => `${v.toFixed(1)} mm*`).join(' × ');
    const fit = $('[data-fit]');
    // Three.js uses Y as the vertical axis; the printer's 260 × 260 × 300 mm
    // build volume therefore maps to X × Z × Y in this viewer.
    const fits = size.x <= 260 && size.z <= 260 && size.y <= 300;
    fit.textContent = fits
      ? 'The current orientation appears to fit within a 260 × 260 × 300 mm build volume. Print setup and bed placement still need checking.'
      : 'The current orientation exceeds the 260 × 260 × 300 mm build volume. Try rotating the model, splitting it or discussing the dimensions with us.';
    fit.classList.toggle('is-warning', !fits);
    if (file) $('[data-file-name]').textContent = file.name;
  }
  function applySelectedColour() {
    if (!currentObject || !colourMap[selectedColour]) return;
    currentObject.traverse((child) => {
      if (!child.isMesh) return;
      const materials = Array.isArray(child.material) ? child.material : [child.material];
      materials.forEach((material) => {
        if (material && material.color) material.color.set(colourMap[selectedColour]);
      });
    });
  }
  function setModel(object, file) {
    if (currentObject) {
      scene.remove(currentObject);
      disposeObject(currentObject);
    }
    object.traverse((child) => {
      if (child.isMesh) {
        if (!child.material || child.material.type === 'MeshBasicMaterial') {
          child.material = new THREE.MeshStandardMaterial({ color: '#aeb9b0', roughness: 0.55 });
        }
        child.material.side = THREE.DoubleSide;
      }
    });
    const box = new THREE.Box3().setFromObject(object);
    if (box.isEmpty()) throw new Error('This file does not contain a visible 3D model.');
    const size = box.getSize(new THREE.Vector3());
    if (![size.x,size.y,size.z].every(Number.isFinite) || Math.max(size.x,size.y,size.z) > 5000) {
      throw new Error('The model dimensions look unusual. Check the units used when exporting the file.');
    }
    const center = box.getCenter(new THREE.Vector3());
    const pivot = new THREE.Group();
    object.position.sub(center);
    pivot.add(object);
    currentObject = pivot;
    currentFile = file || null;
    scene.add(pivot);
    if (colourMap[selectedColour]) applySelectedColour();
    $('[data-placeholder]').hidden = true;
    $('[data-model-info]').hidden = false;
    $('[data-triangles]').textContent = (object.userData.apTriangles || countTriangles(object)).toLocaleString();
    updateModelMetrics(file);
    $('[data-status]').textContent = 'Model preview loaded';
    $('[data-reset]').disabled = false;
    $('[data-reset-orientation]').disabled = false;
    root.querySelectorAll('[data-rotate-axis]').forEach(button => button.disabled = false);
    $('[data-lay-flat]').disabled = false;
    frameObject(pivot, size);
  }
  function clearCurrentModel() {
    if (currentObject) {
      scene.remove(currentObject);
      disposeObject(currentObject);
    }
    currentObject = null;
    currentFile = null;
    $('[data-placeholder]').hidden = false;
    $('[data-model-info]').hidden = true;
    $('[data-dimensions]').textContent = '—';
    $('[data-file-name]').textContent = '—';
    $('[data-triangles]').textContent = '—';
    $('[data-fit]').textContent = '';
    $('[data-fit]').classList.remove('is-warning');
    $('[data-status]').textContent = 'Waiting for a file';
    $('[data-reset]').disabled = true;
    $('[data-reset-orientation]').disabled = true;
    root.querySelectorAll('[data-rotate-axis]').forEach(button => button.disabled = true);
    $('[data-lay-flat]').disabled = true;
  }

  async function refreshPreviewFromSelection() {
    const nextPreview = selectedFiles.find(file => previewable.has(file.name.split('.').pop().toLowerCase()));
    if (!nextPreview) {
      clearCurrentModel();
      $('[data-status]').textContent = selectedFiles.length
        ? 'Files selected · no browser preview available'
        : 'Waiting for a file';
      return;
    }
    try {
      await previewFile(nextPreview);
    } catch (e) {
      clearCurrentModel();
      error(e?.message || 'We could not preview this model. Please check the file and try again.');
    }
  }

  function countTriangles(object) {
    let count = 0;
    object.traverse((child) => {
      if (child.isMesh && child.geometry) {
        const g = child.geometry;
        count += g.index ? g.index.count / 3 : (g.attributes.position?.count || 0) / 3;
      }
    });
    return Math.round(count);
  }
  async function previewFile(file) {
    const ext = file.name.split('.').pop().toLowerCase();
    if (!previewable.has(ext)) {
      $('[data-status]').textContent = 'File selected · preview unavailable';
      return;
    }
    const buffer = await file.arrayBuffer();
    let object;
    if (ext === 'stl') {
      const geometry = new STLLoader().parse(buffer);
      geometry.computeVertexNormals();
      object = new THREE.Mesh(geometry, new THREE.MeshStandardMaterial({ color: '#aeb9b0', roughness: 0.55, side: THREE.DoubleSide }));
    } else if (ext === 'obj') {
      const text = new TextDecoder().decode(buffer);
      object = new OBJLoader().parse(text);
    } else {
      object = new ThreeMFLoader().parse(buffer);
    }
    setModel(object, file);
  }
  function renderFileList() {
    const list = $('[data-file-list]');
    list.replaceChildren();
    selectedFiles.forEach((file, index) => {
      const row = document.createElement('div');
      row.className = 'ap-service__file';
      const name = document.createElement('span');
      name.textContent = `${file.name}${previewable.has(file.name.split('.').pop().toLowerCase()) ? '' : ' · preview not available'}`;
      const size = document.createElement('small');
      size.textContent = `${(file.size / 1024 / 1024).toFixed(2)} MB`;
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'ap-service__remove-file';
      remove.textContent = 'Remove';
      remove.setAttribute('aria-label', `Remove ${file.name}`);
      remove.addEventListener('click', async () => {
        const wasPreviewed = currentFile === file;
        selectedFiles.splice(index, 1);
        uploadedFiles = [];
        renderFileList();
        if (wasPreviewed || !currentObject) await refreshPreviewFromSelection();
      });
      row.append(name, size, remove);
      list.append(row);
    });
    $('[data-clear-files]').hidden = selectedFiles.length === 0;
    $('[data-enquiry]').disabled = selectedFiles.length === 0;
  }
  async function acceptFiles(fileList) {
    clearError();
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    for (const file of incoming) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!allowed.has(ext)) return error(`${file.name}: this file format is not on the accepted list.`);
      if (file.size > maxBytes) return error(`${file.name}: file exceeds the ${Math.round(maxBytes/1024/1024)} MB per-file limit.`);
    }
    selectedFiles = [...selectedFiles, ...incoming].filter((file, i, arr) =>
      arr.findIndex(other => other.name === file.name && other.size === file.size && other.lastModified === file.lastModified) === i
    );uploadedFiles = [];
    renderFileList();
    const firstPreview = selectedFiles.find(f => previewable.has(f.name.split('.').pop().toLowerCase()));
    if (firstPreview) {
      try { await previewFile(firstPreview); }
      catch (e) { error(e?.message || 'We could not preview this model. Please check the file and try again.'); }
    } else {
      $('[data-status]').textContent = 'Files selected · no browser preview available';
    }
  }


  function waitForTurnstile() {
    return new Promise((resolve, reject) => {
      const started = Date.now();
      const timer = setInterval(() => {
        if (window.turnstile) { clearInterval(timer); resolve(window.turnstile); }
        else if (Date.now() - started > 15000) { clearInterval(timer); reject(new Error('Security check could not load. Please refresh and try again.')); }
      }, 150);
    });
  }
  async function setupTurnstile() {
    try {
      const turnstile = await waitForTurnstile();
      if (turnstileWidgetId !== null) return;
      turnstileWidgetId = turnstile.render($('[data-turnstile]'), {
        sitekey,
        theme: 'light',
        callback: token => { turnstileToken = token; clearError(); },
        'expired-callback': () => { turnstileToken = ''; },
        'error-callback': () => { turnstileToken = ''; error('Security check could not be completed. Please try again.'); }
      });
    } catch (e) { error(e.message); }
  }
  async function uploadSelectedFiles() {
    if (!turnstileToken) throw new Error('Please complete the security check before submitting.');
    const form = new FormData();
    selectedFiles.forEach(file => form.append('files', file, file.name));
    form.append('turnstileToken', turnstileToken);
    const response = await fetch(`${uploadEndpoint}/upload`, { method: 'POST', body: form });
    let result;
    try { result = await response.json(); } catch (_) { throw new Error('The upload service returned an unexpected response.'); }
    turnstileToken = '';
    if (turnstileWidgetId !== null && window.turnstile) window.turnstile.reset(turnstileWidgetId);
    if (!response.ok || !result.success) throw new Error(result.error || 'Upload failed. Please try again.');
    uploadedFiles = result.files || [];
    return uploadedFiles;
  }

  initViewer();
  setupTurnstile();
  root.querySelectorAll('[data-tab]').forEach((button) => button.addEventListener('click', () => {
    const tab = button.dataset.tab;
    root.querySelectorAll('[data-tab]').forEach((b) => {
      const active = b === button;
      b.classList.toggle('is-active', active);
      b.setAttribute('aria-selected', String(active));
    });
    root.querySelectorAll('[data-panel]').forEach((panel) => panel.hidden = panel.dataset.panel !== tab);
    if (tab === 'print') resize();
  }));
  filesInput.addEventListener('change', (event) => {
    acceptFiles(event.target.files);
    // Allows customers to select the same file again after removing it.
    filesInput.value = '';
  });
  $('[data-clear-files]').addEventListener('click', () => {
    selectedFiles = [];
    uploadedFiles = [];
    filesInput.value = '';
    renderFileList();
    clearCurrentModel();
    clearError();
  });
  const dropzone = $('[data-dropzone]');
  ['dragenter','dragover'].forEach(type => dropzone.addEventListener(type, e => {
    e.preventDefault(); dropzone.classList.add('is-dragging');
  }));
  ['dragleave','drop'].forEach(type => dropzone.addEventListener(type, e => {
    e.preventDefault(); dropzone.classList.remove('is-dragging');
  }));
  dropzone.addEventListener('drop', e => acceptFiles(e.dataTransfer.files));
  $('[data-reset]').addEventListener('click', () => {
    if (!currentObject) return;
    const box = new THREE.Box3().setFromObject(currentObject);
    frameObject(currentObject, box.getSize(new THREE.Vector3()));
  });
  root.querySelectorAll('[data-rotate-axis]').forEach((button) => button.addEventListener('click', () => {
    if (!currentObject) return;
    const axis = button.dataset.rotateAxis;
    const direction = Number(button.dataset.rotateDir || 1);
    currentObject.rotation[axis] += direction * Math.PI / 2;
    currentObject.rotation[axis] = Math.round(currentObject.rotation[axis] / (Math.PI / 2)) * (Math.PI / 2);
    updateModelMetrics();
  }));
  function findLargestPlanarFaceDirection(object) {
    // Group similarly oriented triangle faces and use the largest planar group
    // as the support face, similar to the automatic lay-flat operation in slicers.
    const faceGroups = new Map();
    object.updateMatrixWorld(true);
    object.traverse((mesh) => {
      if (!mesh.isMesh || !mesh.geometry?.attributes?.position) return;
      const geometry = mesh.geometry;
      const positions = geometry.attributes.position;
      const index = geometry.index;
      const triangleCount = index ? index.count / 3 : positions.count / 3;
      const stride = Math.max(1, Math.ceil(triangleCount / 50000));
      const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3();
      const ab = new THREE.Vector3(), ac = new THREE.Vector3(), normal = new THREE.Vector3();
      for (let face = 0; face < triangleCount; face += stride) {
        const i = face * 3;
        const ia = index ? index.getX(i) : i;
        const ib = index ? index.getX(i + 1) : i + 1;
        const ic = index ? index.getX(i + 2) : i + 2;
        a.fromBufferAttribute(positions, ia).applyMatrix4(mesh.matrixWorld);
        b.fromBufferAttribute(positions, ib).applyMatrix4(mesh.matrixWorld);
        c.fromBufferAttribute(positions, ic).applyMatrix4(mesh.matrixWorld);
        ab.subVectors(b, a); ac.subVectors(c, a);
        normal.crossVectors(ab, ac);
        const doubleArea = normal.length();
        if (doubleArea < 1e-8) continue;
        normal.multiplyScalar(1 / doubleArea);
        // Quantise direction to combine triangles belonging to the same face.
        const key = `${Math.round(normal.x * 40)},${Math.round(normal.y * 40)},${Math.round(normal.z * 40)}`;
        const area = doubleArea * 0.5 * stride;
        const group = faceGroups.get(key) || { normal: normal.clone(), area: 0 };
        group.area += area;
        faceGroups.set(key, group);
      }
    });
    const best = [...faceGroups.values()].sort((a, b) => b.area - a.area)[0];
    return best ? best.normal : new THREE.Vector3(0, 1, 0);
  }

  $('[data-lay-flat]').addEventListener('click', () => {
    if (!currentObject) return;
    clearError();
    // Evaluate the original file orientation each time, not the last preview rotation.
    currentObject.quaternion.identity();
    currentObject.position.set(0, 0, 0);
    const supportNormal = findLargestPlanarFaceDirection(currentObject);
    // The selected support face's outward normal should point down towards the bed.
    const bedNormal = new THREE.Vector3(0, -1, 0);
    currentObject.quaternion.setFromUnitVectors(supportNormal, bedNormal);
    currentObject.updateMatrixWorld(true);
    const bounds = new THREE.Box3().setFromObject(currentObject);
    // Place the lowest point exactly on the virtual print bed.
    currentObject.position.y -= bounds.min.y;
    currentObject.updateMatrixWorld(true);
    const flatBounds = new THREE.Box3().setFromObject(currentObject);
    updateModelMetrics();
    frameObject(currentObject, flatBounds.getSize(new THREE.Vector3()));
    $('[data-status]').textContent = 'Model laid flat · preview only';
  });

  $('[data-reset-orientation]').addEventListener('click', () => {
    if (!currentObject) return;
    currentObject.rotation.set(0, 0, 0);
    currentObject.position.set(0, 0, 0);
    updateModelMetrics();
    const box = new THREE.Box3().setFromObject(currentObject);
    frameObject(currentObject, box.getSize(new THREE.Vector3()));
  });
  $('[data-field="colour"]').addEventListener('change', (event) => {
    selectedColour = event.target.value;
    applySelectedColour();
    });

$('[data-enquiry]').addEventListener('click', async () => {
  clearError();

  const button = $('[data-enquiry]');
  const successMessage = $('[data-enquiry-success]');
  const values = {};

  successMessage.hidden = true;
  successMessage.textContent = '';

  root.querySelectorAll('[data-field]').forEach(el => {
    values[el.dataset.field] = el.value.trim();
  });

  if (!values.customer_name) {
    return error('Please enter your name.');
  }

  if (!values.customer_email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.customer_email)) {
    return error('Please enter a valid email address.');
  }

  if (!selectedFiles.length) {
    return error('Please upload at least one 3D file.');
  }

  if (!uploadedFiles.length && !turnstileToken) {
    return error('Please complete the security check before submitting.');
  }

  button.disabled = true;

  try {
    let stored = uploadedFiles;

    // STEP 1: Upload files to Cloudflare if needed.
    if (!stored.length) {
      button.textContent = 'Uploading files…';
      stored = await uploadSelectedFiles();
    }

    if (!stored.length) {
      throw new Error('No uploaded files were returned.');
    }

    // STEP 2: Prepare the customer enquiry.
    button.textContent = 'Sending enquiry…';

    const lines = [
      'A.PRINTS — 3D PRINTING ENQUIRY',
      `Customer: ${values.customer_name}`,
      `Email: ${values.customer_email}`,
      `Project: ${values.project || 'Not provided'}`,
      `Quantity: ${values.quantity || '1'}`,
      `Material: ${values.material || 'Not sure'}`,
      `Colour: ${values.colour || 'Not specified'}`,
      `Preview orientation (X/Y/Z): ${
        currentObject
          ? [
              currentObject.rotation.x,
              currentObject.rotation.y,
              currentObject.rotation.z
            ].map(v => `${Math.round(v * 180 / Math.PI)}°`).join(' / ')
          : 'Original file orientation'
      }`,
      `Additional requirements: ${values.notes || 'None'}`,
      '',
      'SECURE FILE DOWNLOAD LINKS (expire after 14 days):',
      ...stored.map(file =>
        `${file.fileName} (${(file.fileSize / 1024 / 1024).toFixed(2)} MB): ${file.downloadUrl}`
      )
    ];

    
    // STEP 3: Wait for a fresh Turnstile security token.
    if (!turnstileToken) {
      button.textContent = 'Complete security check…';

      const started = Date.now();

      while (!turnstileToken && Date.now() - started < 120000) {
        await new Promise(resolve => setTimeout(resolve, 250));
      }
    }

    if (!turnstileToken) {
      throw new Error('Please complete the security check and try again.');
    }

    // STEP 4: Send the enquiry through Cloudflare to Resend.
    button.textContent = 'Sending enquiry…';

    const response = await fetch(`${uploadEndpoint}/enquiry`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: values.customer_name,
        email: values.customer_email,
        body: lines.join('\n'),
        turnstileToken: turnstileToken
      })
    });

    let result;

    try {
      result = await response.json();
    } catch (_) {
      throw new Error('The email service returned an unexpected response.');
    }

    // Reset the security check after the verification attempt.
    turnstileToken = '';

    if (turnstileWidgetId !== null && window.turnstile) {
      window.turnstile.reset(turnstileWidgetId);
    }

    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Your enquiry could not be sent. Please try again.');
    }

    // STEP 5: Display success.
    successMessage.textContent =
      'Thank you! Your enquiry has been submitted successfully. We will review your files and get back to you by email.';

    successMessage.hidden = false;

    button.textContent = 'Enquiry sent successfully';
    button.disabled = true;

    $('[data-upload-note]').textContent =
      'Your files are securely stored and your enquiry has been submitted.';


  } catch (e) {
    error(e?.message || 'Something went wrong. Please try again.');

    button.disabled = false;
    button.textContent = 'Upload files & send enquiry';

    if (!uploadedFiles.length) {
      setupTurnstile();
    }
  }
});

  $('[data-copy]').addEventListener('click', async () => {
    const text = $('[data-summary-text]').value;
    try {
      await navigator.clipboard.writeText(text);
      $('[data-copy]').textContent = 'Copied';
      setTimeout(() => $('[data-copy]').textContent = 'Copy enquiry details', 1800);
    } catch (_) {
      $('[data-summary-text]').select();
      document.execCommand('copy');
    }
  });
});